import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { Text } from '@react-three/drei';
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { sky } from '../../render/skyState';
import { beatBridge } from '../../state/beatBridge';
import { useCityStore } from '../../city/cityStore';
import { placeCentre, placeProps, STAGE_Y } from '../../city/cityMotion';
import { serverNow } from '../../services/worldSocket';
import { addDeck, baseGroundY, groundY, groundMeasured, measureGround, onGround } from '../../city/cityGround';
import { useVenueAnchors } from '../../hooks/useVenueAnchors';
import { useWorldStore } from '../../state/worldStore';
import { makeStageScreen } from '../../city/stageScreen';
import StageSign from './StageSign';

// CityPlaces — the open spaces the artists live their days in.
//
// Khari, 2026-10-23: *"You can add buildings and environments if you like, and
// you can improve the styling as well… What you are building now, I would like
// to call 'the first real thing'."* The street grid had pavements and storefronts
// but nowhere to BE: no square to sit in, no grass, no stage. These are the
// places in the city's registry (API services/city/cityPlaces) that the model
// did not already have, built where the overhead map showed open ground:
//
//   THE PLAZA  north-east of the studio — a tiled square, an outdoor stage,
//              string lights, benches facing the stage.
//   THE PARK   south-east of the studio — grass, a gravel path, old trees,
//              benches, a fountain.
//   and small touches at the venue fronts the artists stand outside: crates
//   outside Wax & Vinyl, a produce stand at the Corner Market, a bench by the
//   Listening Room's door.
//
// ⚠ THIS WORLD IS NOT IN METRES: a person is ~0.75 units tall. A bench seat is
// 0.2 off the ground, a lamp 1.7, a tree 1.5 to 2.6.
//
// Cheap on purpose: repeated things are ONE instanced mesh each, the bulbs are
// emissive (bloom does the glow), and the only real lights are the stage's two.

const PLAZA = { at: [96, -22], w: 30, d: 26 };
const STAGE = { at: [96, -35.2], w: 6.4, d: 3.0 };
const PARK = { at: [96, 32], w: 34, d: 24 };
// Paving stones ~0.22 units (≈ half a metre at this world's scale — a person is
// 0.75): 8 stones per texture tile, one tile every 1.76 units.
const PLAZA_REPEAT = [PLAZA.w / 1.76, PLAZA.d / 1.76];

const nightOn = () => THREE.MathUtils.smoothstep(sky.night, 0.12, 0.5);
// How far the new floors sit above the city's own ground, once that ground has
// been measured (city/cityGround). Tune live: ?cityfloor=.
const FLOOR_Y = (() => {
  const q = typeof window !== 'undefined' && parseFloat(new URLSearchParams(window.location.search).get('cityfloor'));
  return Number.isFinite(q) ? q : 0.012;
})();

// ── Procedural textures (one canvas each, made once) ────────────────────────
function makeTexture(w, h, paint, repeat) {
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  paint(c.getContext('2d'), w, h);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  if (repeat) t.repeat.set(repeat[0], repeat[1]);
  t.anisotropy = 4;
  return t;
}
const rnd = (seed) => { let s = seed >>> 0 || 1; return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; }; };

function stoneTexture() {
  return makeTexture(512, 512, (g, w, h) => {
    const r = rnd(7);
    g.fillStyle = '#6d6259'; g.fillRect(0, 0, w, h);
    const n = 8; const s = w / n;
    for (let y = 0; y < n; y++) {
      for (let x = 0; x < n; x++) {
        const off = (y % 2) * s * 0.5;
        const l = 40 + r() * 16; const hue = 26 + r() * 10;
        g.fillStyle = `hsl(${hue} 14% ${l}%)`;
        g.fillRect(x * s + off + 3, y * s + 3, s - 6, s - 6);
        g.fillRect(x * s + off + 3 - w, y * s + 3, s - 6, s - 6);
      }
    }
    for (let i = 0; i < 2600; i++) { g.fillStyle = `rgba(0,0,0,${r() * 0.06})`; g.fillRect(r() * w, r() * h, 2, 2); }
  }, [PLAZA_REPEAT[0], PLAZA_REPEAT[1]]);
}
// The stage's back wall: an LED wall in a warm wash — a soft sun rising out of
// the floor, light falling in shafts, the pixel grid just visible up close.
function backdropTexture() {
  return makeTexture(512, 256, (g, w, h) => {
    g.fillStyle = '#07040a'; g.fillRect(0, 0, w, h);
    const sun = g.createRadialGradient(w / 2, h * 1.05, 10, w / 2, h * 1.05, w * 0.62);
    sun.addColorStop(0, 'rgba(255,214,150,1)'); sun.addColorStop(0.28, 'rgba(255,128,64,0.9)');
    sun.addColorStop(0.6, 'rgba(170,40,90,0.55)'); sun.addColorStop(1, 'rgba(30,8,40,0)');
    g.fillStyle = sun; g.fillRect(0, 0, w, h);
    g.globalCompositeOperation = 'lighter';
    for (let i = 0; i < 9; i++) {
      const x = w * (0.08 + i * 0.105); const lean = (i - 4) * 9;
      const shaft = g.createLinearGradient(0, 0, 0, h);
      shaft.addColorStop(0, 'rgba(255,190,120,0.16)'); shaft.addColorStop(1, 'rgba(255,190,120,0)');
      g.fillStyle = shaft;
      g.beginPath(); g.moveTo(x - 6, 0); g.lineTo(x + 6, 0); g.lineTo(x + 26 + lean, h); g.lineTo(x - 26 + lean, h); g.closePath(); g.fill();
    }
    g.globalCompositeOperation = 'source-over';
    g.fillStyle = 'rgba(0,0,0,0.38)';
    for (let x = 0; x < w; x += 4) g.fillRect(x, 0, 1, h);
    for (let y = 0; y < h; y += 4) g.fillRect(0, y, w, 1);
  });
}
function grassTexture() {
  return makeTexture(512, 512, (g, w, h) => {
    const r = rnd(11);
    g.fillStyle = '#3f5a2c'; g.fillRect(0, 0, w, h);
    for (let i = 0; i < 9000; i++) {
      const l = 22 + r() * 22; const hue = 82 + r() * 26;
      g.fillStyle = `hsla(${hue} 38% ${l}% / ${0.35 + r() * 0.4})`;
      g.fillRect(r() * w, r() * h, 1 + r() * 2, 2 + r() * 4);
    }
  }, [7, 5]);
}
function gravelTexture() {
  return makeTexture(256, 256, (g, w, h) => {
    const r = rnd(3);
    g.fillStyle = '#a99a84'; g.fillRect(0, 0, w, h);
    for (let i = 0; i < 2600; i++) { const l = 48 + r() * 30; g.fillStyle = `hsl(35 16% ${l}%)`; g.fillRect(r() * w, r() * h, 1 + r() * 2.5, 1 + r() * 2.5); }
  }, [1, 10]);
}

// ── Shared pieces ───────────────────────────────────────────────────────────
// LIGHT ON THE GROUND, WITHOUT LIGHTS. The bulbs and lamps are emissive (bloom
// makes them glow) but light nothing, so after dark the floors under them went
// black. A real light per lamp is out of the question on a phone; a soft warm
// disc laid on the floor, added on top and faded in with the night, reads the
// same from eye height and costs one quad.
let POOL_TEX = null;
function poolTexture() {
  if (POOL_TEX) return POOL_TEX;
  const c = document.createElement('canvas'); c.width = c.height = 128;
  const g = c.getContext('2d');
  const r = g.createRadialGradient(64, 64, 0, 64, 64, 64);
  r.addColorStop(0, 'rgba(255,255,255,1)'); r.addColorStop(0.35, 'rgba(255,255,255,0.55)');
  r.addColorStop(0.7, 'rgba(255,255,255,0.15)'); r.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = r; g.fillRect(0, 0, 128, 128);
  POOL_TEX = new THREE.CanvasTexture(c);
  return POOL_TEX;
}
function poolMaterial(color) {
  return new THREE.MeshBasicMaterial({
    map: poolTexture(), color, transparent: true, opacity: 0, depthWrite: false,
    blending: THREE.AdditiveBlending, toneMapped: false,
    polygonOffset: true, polygonOffsetFactor: -6, polygonOffsetUnits: -6,
  });
}
/** Discs of light on the floor: [x, z, radius] each — one draw for all of them. */
const POOL_GEO = typeof document !== 'undefined' ? new THREE.PlaneGeometry(2, 2).rotateX(-Math.PI / 2) : null;
function Pools({ pools, material, y }) {
  const items = useMemo(() => pools.map(([x, z, r]) => [x, y, z, 0, r]), [pools, y]);
  return <Instances geometry={POOL_GEO} material={material} items={items} castShadow={false} receiveShadow={false} renderOrder={3} />;
}

function benchGeometry() {
  const seat = new THREE.BoxGeometry(0.86, 0.05, 0.24); seat.translate(0, 0.2, 0);
  const back = new THREE.BoxGeometry(0.86, 0.18, 0.04); back.translate(0, 0.33, -0.11);
  const legA = new THREE.BoxGeometry(0.05, 0.2, 0.22); legA.translate(-0.36, 0.1, 0);
  const legB = legA.clone(); legB.translate(0.72, 0, 0);
  return mergeGeometries([seat, back, legA, legB].map((g) => g.toNonIndexed()));
}

// `faces`: its front is +z (city/facing), so ?facing=1 draws it (FacingOverlay).
/** One instanced mesh from a geometry and a list of [x, y, z, rotY, scale]
 *  (scale a number, or [sx, sy, sz]). */
function Instances({ geometry, material, items, castShadow = true, receiveShadow = true, renderOrder, faces }) {
  const ref = useRef();
  useEffect(() => {
    const m = ref.current; if (!m) return;
    const o = new THREE.Object3D();
    items.forEach(([x, y, z, ry = 0, s = 1], i) => {
      o.position.set(x, y, z); o.rotation.set(0, ry, 0);
      if (Array.isArray(s)) o.scale.set(s[0], s[1], s[2]); else o.scale.setScalar(s);
      o.updateMatrix();
      m.setMatrixAt(i, o.matrix);
    });
    m.instanceMatrix.needsUpdate = true;
    m.computeBoundingSphere();
  }, [items]);
  return <instancedMesh ref={ref} args={[geometry, material, items.length]} castShadow={castShadow} receiveShadow={receiveShadow} renderOrder={renderOrder} userData={faces ? { faces: 'prop' } : undefined} />;
}

function Lamp({ at, glowRef }) {
  return (
    <group position={[at[0], 0, at[1]]}>
      <mesh position={[0, 0.85, 0]} castShadow>
        <cylinderGeometry args={[0.025, 0.035, 1.7, 8]} />
        <meshStandardMaterial color="#1c1d22" roughness={0.6} metalness={0.6} />
      </mesh>
      <mesh position={[0, 1.72, 0]}>
        <sphereGeometry args={[0.09, 16, 12]} />
        <meshStandardMaterial ref={glowRef} color="#fff2d6" emissive="#ffcf86" emissiveIntensity={0.2} />
      </mesh>
    </group>
  );
}

// ── THE PLAZA ───────────────────────────────────────────────────────────────
function Plaza({ live }) {
  const stone = useMemo(() => stoneTexture(), []);
  const bench = useMemo(() => benchGeometry(), []);
  const benchMat = useMemo(() => new THREE.MeshStandardMaterial({ color: '#7a5a3a', roughness: 0.7 }), []);
  const bulbGeo = useMemo(() => new THREE.SphereGeometry(0.035, 8, 6), []);
  const bulbMat = useMemo(() => new THREE.MeshStandardMaterial({ color: '#fff3d1', emissive: '#ffc46b', emissiveIntensity: 0.2 }), []);
  const planterGeo = useMemo(() => new THREE.BoxGeometry(0.7, 0.32, 0.7), []);
  const planterMat = useMemo(() => new THREE.MeshStandardMaterial({ color: '#5b5550', roughness: 0.9 }), []);
  const shrubGeo = useMemo(() => new THREE.IcosahedronGeometry(0.42, 1), []);
  const shrubMat = useMemo(() => new THREE.MeshStandardMaterial({ color: '#3e6b34', roughness: 0.85, flatShading: true }), []);
  const lampGlow = [useRef(), useRef(), useRef(), useRef()];
  const spotA = useRef(); const spotB = useRef(); const washRef = useRef();
  const signMat = useMemo(() => new THREE.MeshStandardMaterial({ color: '#ffe2b8', emissive: '#ffb35c', emissiveIntensity: 0.8, toneMapped: false }), []);
  const backdrop = useMemo(() => backdropTexture(), []);
  // During a set (and the five minutes before) the LED wall shows the
  // performer: their name, the song, their covers (city/stageScreen).
  const screen = useMemo(() => makeStageScreen(), []);
  useEffect(() => () => screen.dispose(), [screen]);
  const camera = useThree((st) => st.camera);
  const [cx, cz] = PLAZA.at;

  // Benches in two arcs facing the stage, and a row along each side.
  // ⚠ FACING (2026-10-30, Khari: "the benches at the plaza stage are not facing
  // the stage"): benchGeometry's backrest is on its −z side, so whoever sits
  // looks down +z, and a turn of atan2(dx, dz) points +z at (dx, dz). These
  // carried an extra half turn and every bench had its back to the show; the side
  // rows likewise faced out of the plaza. (The park's and the court's were right.)
  const benches = useMemo(() => {
    const out = [];
    for (const [radius, n] of [[7, 5], [9.6, 6]]) {
      for (let i = 0; i < n; i++) {
        const a = -0.55 + (1.1 * i) / (n - 1);
        const x = STAGE.at[0] + Math.sin(a) * radius;
        const z = STAGE.at[1] + Math.cos(a) * radius;
        out.push([x, 0, z, Math.atan2(STAGE.at[0] - x, STAGE.at[1] - z), 1]);
      }
    }
    for (const side of [-1, 1]) for (let k = 0; k < 3; k++) out.push([cx + side * 13.2, 0, cz - 4 + k * 4.2, -side * Math.PI / 2, 1]);
    return out;
  }, [cx, cz]);

  // String lights: poles around the audience, a sagging line between each pair.
  const poles = useMemo(() => [[-12, -9], [-12, 2], [-12, 11], [12, 11], [12, 2], [12, -9]].map(([x, z]) => [cx + x, cz + z]), [cx, cz]);
  const strands = useMemo(() => {
    const lines = []; const bulbs = [];
    // Not [0, 5]: that strand ran along the stage front and, from the
    // audience's eye height, hung a bulb over the sign.
    const pairs = [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [1, 4], [0, 4], [1, 5], [2, 4], [1, 3]];
    for (const [i, j] of pairs) {
      const a = new THREE.Vector3(poles[i][0], 1.55, poles[i][1]);
      const b = new THREE.Vector3(poles[j][0], 1.55, poles[j][1]);
      const mid = a.clone().add(b).multiplyScalar(0.5); mid.y -= 0.35 + a.distanceTo(b) * 0.012;
      const curve = new THREE.QuadraticBezierCurve3(a, mid, b);
      lines.push(curve);
      const n = Math.max(6, Math.round(a.distanceTo(b) / 1.1));
      for (let k = 1; k < n; k++) { const p = curve.getPoint(k / n); bulbs.push([p.x, p.y - 0.05, p.z, 0, 1]); }
    }
    return { lines, bulbs };
  }, [poles]);
  const wireMat = useMemo(() => new THREE.MeshBasicMaterial({ color: '#141418' }), []);
  const poolMat = useMemo(() => poolMaterial('#ffb867'), []);
  const spillMat = useMemo(() => poolMaterial('#ff7a52'), []);
  const pools = useMemo(() => {
    const out = [];
    // Under the strands: a soft wash every few bulbs.
    strands.bulbs.forEach(([x, , z], i) => { if (i % 4 === 0) out.push([x, z, 2.4]); });
    for (const [x, z] of [[cx - 6, cz + 12.4], [cx + 6, cz + 12.4], [cx - 14, cz - 1], [cx + 14, cz - 1]]) out.push([x, z, 2.6]);
    return out;
  }, [strands, cx, cz]);

  useFrame(() => {
    const on = nightOn();
    const pulse = live ? 0.75 + 0.25 * Math.max(0, Math.sin(beatBridge.phase * Math.PI * 2)) : 1;
    bulbMat.emissiveIntensity = 0.15 + on * 2.6;
    for (const r of lampGlow) if (r.current) r.current.emissiveIntensity = 0.2 + on * 3.2;
    const stageI = (live ? 1 : 0.25 + on * 0.5) * pulse;
    if (spotA.current) spotA.current.intensity = stageI * 14;
    if (spotB.current) spotB.current.intensity = stageI * 14;
    // THE WALL: the performer's screen while a set is on (or about to be) and
    // someone is near enough to see it; otherwise its own warm wash.
    // ⚠ THE SET ON NOW FIRST (Khari, 2026-10-30: "The plaza's stage monitor
    // should not show previous or next artist during a performance. the smaller
    // 'up next' monitor can remain."): sets now end when their music and words
    // do, with the next a minute later, so the next one's five-minute "up next"
    // ran over the set being played and whichever came first in the list won.
    // Up next only once nobody is on; the last set lingers 15 s only if nobody is due.
    const cs = useCityStore.getState();
    const t = serverNow();
    const plaza = (cs.shows || []).filter((x) => (x.place || 'street-plaza') === 'street-plaza');
    const show = plaza.find((x) => t >= x.t0 && t < x.t1)
      || plaza.filter((x) => t < x.t0 && t >= x.t0 - 5 * 60000).sort((a, b) => a.t0 - b.t0)[0]
      || plaza.find((x) => t >= x.t1 && t < x.t1 + 15000)
      || null;
    let screenOn = false;
    if (show && Math.hypot(camera.position.x - STAGE.at[0], camera.position.z - STAGE.at[1]) < 70) {
      const face = (useWorldStore.getState().npcList || []).find((n) => Number(n.avatarId) === Number(show.a))?.headshotUrl || null;
      screenOn = screen.draw(show, t, face);
    }
    const wall = washRef.current;
    if (wall) {
      const map = screenOn ? screen.texture : backdrop;
      if (wall.emissiveMap !== map) { wall.emissiveMap = map; wall.needsUpdate = true; }
      wall.emissiveIntensity = screenOn ? 1.05 + on * 0.15 : (live ? 2.0 : 0.75 + on * 0.75) * pulse;
    }
    signMat.emissiveIntensity = 0.6 + on * 1.8;
    poolMat.opacity = on * 0.11;
    spillMat.opacity = on * (live ? 0.5 : 0.28) * pulse;
  });

  return (
    <group>
      {/* The square itself, a hair above the ground. */}
      {/* ⚠ polygonOffset: the city's own ground is a hair above y=0 out here,
          and a floor laid flush with it z-fights into invisibility. */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[cx, FLOOR_Y, cz]} receiveShadow renderOrder={1}>
        <planeGeometry args={[PLAZA.w, PLAZA.d]} />
        <meshStandardMaterial map={stone} roughness={0.85} metalness={0.02} polygonOffset polygonOffsetFactor={-2} polygonOffsetUnits={-2} />
      </mesh>
      {/* A low kerb around it, so it reads as a place rather than a texture. */}
      {[[0, -PLAZA.d / 2, PLAZA.w, 0.18], [0, PLAZA.d / 2, PLAZA.w, 0.18], [-PLAZA.w / 2, 0, 0.18, PLAZA.d], [PLAZA.w / 2, 0, 0.18, PLAZA.d]].map(([x, z, w, d], i) => (
        <mesh key={i} position={[cx + x, 0.04, cz + z]} receiveShadow castShadow>
          <boxGeometry args={[w, 0.08, d]} />
          <meshStandardMaterial color="#8a7f73" roughness={0.9} />
        </mesh>
      ))}

      {/* THE STAGE — facing the square (+z). */}
      <group position={[STAGE.at[0], 0, STAGE.at[1]]}>
        <mesh position={[0, STAGE_Y / 2, -0.2]} castShadow receiveShadow>
          <boxGeometry args={[STAGE.w, STAGE_Y, STAGE.d]} />
          <meshStandardMaterial color="#2a2522" roughness={0.75} />
        </mesh>
        <mesh position={[0, STAGE_Y + 0.003, -0.2]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
          <planeGeometry args={[STAGE.w - 0.1, STAGE.d - 0.1]} />
          <meshStandardMaterial color="#3a2f28" roughness={0.55} metalness={0.05} />
        </mesh>
        {/* Steps up, both sides. */}
        {[-1, 1].map((s) => (
          <mesh key={s} position={[s * (STAGE.w / 2 + 0.25), STAGE_Y / 4, 0.6]} castShadow receiveShadow>
            <boxGeometry args={[0.5, STAGE_Y / 2, 0.8]} />
            <meshStandardMaterial color="#2a2522" roughness={0.75} />
          </mesh>
        ))}
        {/* Backdrop and its warm wash. */}
        <mesh position={[0, 1.25, -1.62]} castShadow>
          <boxGeometry args={[STAGE.w + 0.4, 2.5, 0.08]} />
          <meshStandardMaterial color="#141217" roughness={0.95} />
        </mesh>
        <mesh position={[0, 1.15, -1.57]}>
          <planeGeometry args={[STAGE.w - 0.6, 1.7]} />
          <meshStandardMaterial ref={washRef} color="#000000" emissive="#ffffff" emissiveMap={backdrop} emissiveIntensity={0.4} roughness={1} />
        </mesh>
        {/* Truss: two towers and a top bar, with lamp cans along it. */}
        {[-1, 1].map((s) => (
          <mesh key={s} position={[s * (STAGE.w / 2 - 0.1), 1.35, 0.9]} castShadow>
            <boxGeometry args={[0.12, 2.7, 0.12]} />
            <meshStandardMaterial color="#9aa0a8" roughness={0.4} metalness={0.8} />
          </mesh>
        ))}
        <mesh position={[0, 2.66, 0.9]} castShadow>
          <boxGeometry args={[STAGE.w - 0.1, 0.12, 0.12]} />
          <meshStandardMaterial color="#9aa0a8" roughness={0.4} metalness={0.8} />
        </mesh>
        {[-2.2, -0.75, 0.75, 2.2].map((x) => (
          <mesh key={x} position={[x, 2.52, 0.9]} rotation={[0.5, 0, 0]}>
            <cylinderGeometry args={[0.07, 0.1, 0.18, 10]} />
            <meshStandardMaterial color="#222" emissive="#ffd9a0" emissiveIntensity={0.6} />
          </mesh>
        ))}
        {/* The stage's only real lights: warm from stage-left, rose from stage-right. */}
        <pointLight ref={spotA} position={[-1.6, 2.2, 1.4]} distance={7} decay={1.6} color="#ffd2a0" intensity={4} />
        <pointLight ref={spotB} position={[1.6, 2.2, 1.4]} distance={7} decay={1.6} color="#ffb3d2" intensity={4} />
        {/* Speakers. */}
        {[-1, 1].map((s) => (
          <group key={s} position={[s * (STAGE.w / 2 + 0.55), 0, -0.6]}>
            <mesh position={[0, 0.45, 0]} castShadow>
              <boxGeometry args={[0.55, 0.9, 0.5]} />
              <meshStandardMaterial color="#121214" roughness={0.6} />
            </mesh>
            {[0.28, 0.66].map((y) => (
              <mesh key={y} position={[0, y, 0.255]}>
                <circleGeometry args={[0.16, 20]} />
                <meshStandardMaterial color="#26262b" roughness={0.4} metalness={0.3} />
              </mesh>
            ))}
          </group>
        ))}
        {/* The name, over the stage. */}
        <Text position={[0, 2.95, 0.9]} fontSize={0.34} letterSpacing={0.18} anchorX="center" anchorY="middle" material={signMat}>
          THE PLAZA
        </Text>
      </group>

      {/* Seating, planters, lamps. */}
      <Instances geometry={bench} material={benchMat} items={benches} faces />
      <Instances geometry={planterGeo} material={planterMat} items={[[cx - 13.4, 0.16, cz - 11.5], [cx + 13.4, 0.16, cz - 11.5], [cx - 13.4, 0.16, cz + 11.5], [cx + 13.4, 0.16, cz + 11.5]]} />
      <Instances geometry={shrubGeo} material={shrubMat} items={[[cx - 13.4, 0.66, cz - 11.5], [cx + 13.4, 0.66, cz - 11.5], [cx - 13.4, 0.66, cz + 11.5], [cx + 13.4, 0.66, cz + 11.5]]} />
      {[[cx - 6, cz + 12.4], [cx + 6, cz + 12.4], [cx - 14, cz - 1], [cx + 14, cz - 1]].map((p, i) => <Lamp key={i} at={p} glowRef={lampGlow[i]} />)}

      {/* String lights. */}
      {poles.map(([x, z], i) => (
        <mesh key={i} position={[x, 0.8, z]} castShadow>
          <cylinderGeometry args={[0.025, 0.03, 1.6, 6]} />
          <meshStandardMaterial color="#1e1f24" roughness={0.6} metalness={0.5} />
        </mesh>
      ))}
      {strands.lines.map((curve, i) => (
        <mesh key={i} material={wireMat}>
          <tubeGeometry args={[curve, 16, 0.006, 4, false]} />
        </mesh>
      ))}
      <Instances geometry={bulbGeo} material={bulbMat} items={strands.bulbs} castShadow={false} />
      <Pools pools={pools} material={poolMat} y={FLOOR_Y + 0.02} />
      {/* The stage's glow spilling out onto the front rows. */}
      <Pools pools={[[STAGE.at[0], STAGE.at[1] + 4.2, 5.5]]} material={spillMat} y={FLOOR_Y + 0.025} />
    </group>
  );
}

// ── THE PARK ────────────────────────────────────────────────────────────────
function Park() {
  const grass = useMemo(() => grassTexture(), []);
  const gravel = useMemo(() => gravelTexture(), []);
  const bench = useMemo(() => benchGeometry(), []);
  const benchMat = useMemo(() => new THREE.MeshStandardMaterial({ color: '#6b4b30', roughness: 0.75 }), []);
  const trunkGeo = useMemo(() => { const g = new THREE.CylinderGeometry(0.05, 0.08, 1, 7); g.translate(0, 0.5, 0); return g; }, []);
  const trunkMat = useMemo(() => new THREE.MeshStandardMaterial({ color: '#4a3526', roughness: 0.95 }), []);
  const crownGeo = useMemo(() => new THREE.IcosahedronGeometry(0.6, 1), []);
  const crownMats = useMemo(() => ['#3d6a33', '#4f7a3a', '#365f2e'].map((c) => new THREE.MeshStandardMaterial({ color: c, roughness: 0.9, flatShading: true })), []);
  const bushGeo = useMemo(() => new THREE.IcosahedronGeometry(0.28, 1), []);
  const lampGlow = [useRef(), useRef(), useRef(), useRef(), useRef()];
  const waterRef = useRef();
  const [cx, cz] = PARK.at;
  const lamps = useMemo(() => [[cx - 9, cz - 5.6], [cx + 3.6, cz + 1.4], [cx + 8.4, cz + 9], [cx - 12.5, cz + 4.5], [cx + 13, cz - 3]], [cx, cz]);
  const poolMat = useMemo(() => poolMaterial('#ffc27a'), []);
  const waterPool = useMemo(() => poolMaterial('#6fc4ff'), []);
  const grassMat = useRef();

  // A gravel path that curves through the grass, west edge to south edge.
  const path = useMemo(() => new THREE.CatmullRomCurve3([
    new THREE.Vector3(cx - 17, 0.02, cz - 6), new THREE.Vector3(cx - 7, 0.02, cz - 3),
    new THREE.Vector3(cx, 0.02, cz), new THREE.Vector3(cx + 6, 0.02, cz + 4), new THREE.Vector3(cx + 9, 0.02, cz + 12),
  ]), [cx, cz]);
  const pathGeo = useMemo(() => {
    // A flat ribbon along the curve, 1.6 wide.
    const pts = path.getSpacedPoints(80); const pos = []; const uv = []; const idx = [];
    for (let i = 0; i < pts.length; i++) {
      const t = path.getTangentAt(i / (pts.length - 1)); const n = new THREE.Vector3(-t.z, 0, t.x).normalize().multiplyScalar(0.8);
      pos.push(pts[i].x + n.x, FLOOR_Y + 0.012, pts[i].z + n.z, pts[i].x - n.x, FLOOR_Y + 0.012, pts[i].z - n.z);
      uv.push(0, i / 4, 1, i / 4);
      if (i) { const a = (i - 1) * 2; idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2); }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    g.setIndex(idx); g.computeVertexNormals();
    return g;
  }, [path]);

  const trees = useMemo(() => {
    const r = rnd(29); const out = [];
    const spots = [[-14, 7], [-11, -8], [-6, 8], [-3, -9], [3, -8], [8, -6], [13, -7], [14, 4], [3, 9], [-9, 2], [11, 9], [-15, -2], [6, 3], [-1, 6]];
    for (const [x, z] of spots) out.push({ x: cx + x + (r() - 0.5), z: cz + z + (r() - 0.5), h: 1.2 + r() * 1.3, k: Math.floor(r() * 3) });
    return out;
  }, [cx, cz]);

  const benches = useMemo(() => {
    const out = [];
    for (const u of [0.12, 0.3, 0.55, 0.78]) {
      const p = path.getPointAt(u); const t = path.getTangentAt(u);
      const n = new THREE.Vector3(-t.z, 0, t.x).normalize();
      const side = u < 0.4 ? 1 : -1;
      const q = p.clone().addScaledVector(n, side * 1.35);
      out.push([q.x, 0, q.z, Math.atan2(-n.x * side, -n.z * side), 1]);
    }
    return out;
  }, [path]);

  useFrame(({ clock }) => {
    const on = nightOn();
    for (const r of lampGlow) if (r.current) r.current.emissiveIntensity = 0.2 + on * 3.2;
    if (waterRef.current) waterRef.current.emissiveIntensity = 0.12 + on * 0.5 + 0.05 * Math.sin(clock.elapsedTime * 2);
    poolMat.opacity = on * 0.22;
    if (grassMat.current) grassMat.current.emissiveIntensity = on * 0.14;
    waterPool.opacity = on * (0.16 + 0.03 * Math.sin(clock.elapsedTime * 1.3));
  });

  return (
    <group>
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[cx, FLOOR_Y, cz]} receiveShadow renderOrder={1}>
        <planeGeometry args={[PARK.w, PARK.d]} />
        {/* After dark the grass keeps a little of its own colour (moonlight, the
            city's glow) — without it the park floor read as a black hole. */}
        <meshStandardMaterial ref={grassMat} map={grass} emissive="#ffffff" emissiveMap={grass} emissiveIntensity={0} roughness={0.95} polygonOffset polygonOffsetFactor={-2} polygonOffsetUnits={-2} />
      </mesh>
      <mesh geometry={pathGeo} receiveShadow renderOrder={2}>
        <meshStandardMaterial map={gravel} roughness={1} polygonOffset polygonOffsetFactor={-4} polygonOffsetUnits={-4} />
      </mesh>
      {/* Trees: one draw for the trunks, one per crown colour. */}
      <Instances geometry={trunkGeo} material={trunkMat} items={trees.map((t) => [t.x, 0, t.z, 0, [1, t.h * 0.55, 1]])} />
      {crownMats.map((mat, k) => (
        <Instances key={k} geometry={crownGeo} material={mat} items={[
          ...trees.filter((t) => t.k === k).map((t) => [t.x, t.h * 0.75, t.z, 0, t.h * 0.62]),
          ...trees.filter((t) => (t.k + 1) % 3 === k).map((t) => [t.x + 0.22 * t.h, t.h * 0.58, t.z + 0.1, 0, t.h * 0.4]),
        ]} />
      ))}
      <Instances geometry={bench} material={benchMat} items={benches} faces />
      <Instances geometry={bushGeo} material={crownMats[1]} items={[[cx - 16, 0.18, cz + 10], [cx - 15.3, 0.16, cz + 10.6], [cx + 16, 0.18, cz - 10], [cx + 15.2, 0.15, cz - 10.4], [cx + 4, 0.16, cz - 10.5], [cx - 8, 0.16, cz + 10.5]]} />
      {/* The fountain, where the path bends. */}
      <group position={[cx + 1.8, 0, cz - 2.6]}>
        <mesh position={[0, 0.12, 0]} castShadow receiveShadow>
          <cylinderGeometry args={[1.05, 1.15, 0.24, 28, 1, true]} />
          <meshStandardMaterial color="#8d8478" roughness={0.85} side={THREE.DoubleSide} />
        </mesh>
        <mesh position={[0, 0.2, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <circleGeometry args={[1.0, 28]} />
          <meshStandardMaterial ref={waterRef} color="#2f5f78" emissive="#4aa3d6" emissiveIntensity={0.2} roughness={0.1} metalness={0.2} />
        </mesh>
        <mesh position={[0, 0.5, 0]} castShadow>
          <cylinderGeometry args={[0.12, 0.18, 0.6, 12]} />
          <meshStandardMaterial color="#9a9184" roughness={0.8} />
        </mesh>
      </group>
      {lamps.map((p, i) => <Lamp key={i} at={p} glowRef={lampGlow[i]} />)}
      <Pools pools={lamps.map(([x, z]) => [x, z, 3.2])} material={poolMat} y={FLOOR_Y + 0.03} />
      <Pools pools={[[cx + 1.8, cz - 2.6, 2.6]]} material={waterPool} y={FLOOR_Y + 0.03} />
    </group>
  );
}

// ── Touches at the venue fronts ─────────────────────────────────────────────
function VenueTouches({ ground }) {
  const anchors = useVenueAnchors();
  const places = useCityStore((s) => s.snap?.places || null);
  /* A spot ALONG a venue's pavement (`line` axis), past where people stand:
     most fronts are a narrow pavement, so a prop belongs in the line, turned
     to run with it, never out in the road. Returns [x, z, rotY]. */
  const along = (slug, d) => {
    const p = places?.find((x) => x.slug === slug);
    if (!p) return null;
    const c = placeCentre(p);
    if (p.line === 'z') return [c[0], c[1] + d, Math.PI / 2];
    if (p.line === 'x') return [c[0] + d, c[1], 0];
    return [c[0] + d, c[1], 0];
  };
  const crateMat = useMemo(() => new THREE.MeshStandardMaterial({ color: '#8b6a45', roughness: 0.8 }), []);
  const sleeveColors = ['#d24b3a', '#2d6cdf', '#e8b931', '#1f1f24', '#7a3fa0', '#2f9e6b'];
  const fruitColors = ['#e8452e', '#f2a32c', '#7cc243', '#f5d33b', '#a63b8a'];
  if (!places || !anchors) return null;
  // `ground` changes once the ground is measured, which re-renders these onto it.
  void ground;
  const Placed = ({ at, children }) => <group position={[at[0], groundY(at[0], at[1]), at[1]]} rotation={[0, at[2], 0]} userData={{ faces: 'prop' }}>{children}</group>;

  // What each kind of prop looks like (its front is local +z, city/facing).
  const draw = {
    // Crates of records on a trestle outside Wax & Vinyl.
    crates: () => (
      <>
        <mesh position={[0, 0.16, 0]} material={crateMat} castShadow receiveShadow><boxGeometry args={[1.0, 0.04, 0.4]} /></mesh>
        {[-0.38, 0.38].map((x) => <mesh key={x} position={[x, 0.08, 0]} material={crateMat} castShadow><boxGeometry args={[0.04, 0.16, 0.36]} /></mesh>)}
        {[-0.25, 0.25].map((x, i) => (
          <group key={x} position={[x, 0.26, 0]}>
            <mesh material={crateMat} castShadow><boxGeometry args={[0.4, 0.16, 0.34]} /></mesh>
            {[0, 1, 2, 3, 4].map((k) => (
              <mesh key={k} position={[0, 0.1, -0.12 + k * 0.06]} rotation={[-0.25, 0, 0]} castShadow>
                <boxGeometry args={[0.32, 0.32, 0.008]} />
                <meshStandardMaterial color={sleeveColors[(k + i * 2) % sleeveColors.length]} roughness={0.6} />
              </mesh>
            ))}
          </group>
        ))}
      </>
    ),
    // A produce stand outside the Corner Market.
    produce: () => (
      <>
        <mesh position={[0, 0.18, 0]} rotation={[0.35, 0, 0]} material={crateMat} castShadow receiveShadow><boxGeometry args={[1.1, 0.06, 0.5]} /></mesh>
        {[-0.45, 0.45].map((x) => <mesh key={x} position={[x, 0.1, 0.05]} material={crateMat} castShadow><boxGeometry args={[0.05, 0.2, 0.4]} /></mesh>)}
        {Array.from({ length: 18 }).map((_, k) => (
          <mesh key={k} position={[-0.42 + (k % 6) * 0.17, 0.25 + Math.floor(k / 6) * 0.04, -0.12 + Math.floor(k / 6) * 0.13]} castShadow>
            <sphereGeometry args={[0.055, 10, 8]} />
            <meshStandardMaterial color={fruitColors[(k + Math.floor(k / 6)) % fruitColors.length]} roughness={0.5} />
          </mesh>
        ))}
      </>
    ),
    bench: (place) => (
      <mesh geometry={BENCH} castShadow receiveShadow>
        <meshStandardMaterial color={place.kind === 'outdoor' ? '#3d5a7a' : '#6e4f34'} roughness={0.7} />
      </mesh>
    ),
    // A lit poster stand outside the Cinema.
    poster: () => (
      <>
        <mesh position={[0, 0.42, 0]} castShadow><boxGeometry args={[0.5, 0.84, 0.06]} /><meshStandardMaterial color="#1a1a1f" roughness={0.5} metalness={0.4} /></mesh>
        <mesh position={[0, 0.44, 0.032]}><planeGeometry args={[0.42, 0.7]} /><meshStandardMaterial color="#2a1630" emissive="#c2457a" emissiveIntensity={0.9} /></mesh>
      </>
    ),
    // Road cases waiting at the stage door.
    cases: () => (
      <>
        {[[0, 0.14, 0, 0.6, 0.28, 0.4], [0.05, 0.38, 0, 0.5, 0.2, 0.36], [0.7, 0.18, 0.05, 0.4, 0.36, 0.4]].map(([x, y, z, w, h, d], k) => (
          <mesh key={k} position={[x, y, z]} castShadow receiveShadow>
            <boxGeometry args={[w, h, d]} />
            <meshStandardMaterial color="#1b1b1e" roughness={0.45} metalness={0.5} />
          </mesh>
        ))}
      </>
    ),
  };

  // THE PLACES' OWN PROPS (API cityPlaces `props`, 2026-10-30), where the city
  // also sends people to use them (city/cityMotion placeProps + `u` spots). An
  // API from before that sends none: the old fixed set, so nothing disappears.
  const listed = places.some((p) => p.props?.length);
  const items = [];
  if (listed) {
    for (const p of places) placeProps(p).forEach((pr, i) => { if (draw[pr.kind]) items.push({ key: `${p.slug}:${i}`, at: [pr.at[0], pr.at[1], pr.rotY], kind: pr.kind, place: p }); });
  } else {
    for (const [slug, d, kind] of [['record-store', -4.3, 'crates'], ['grocery', 4.3, 'produce'], ['listening-room', 4.2, 'bench'], ['theater', 4.2, 'poster'], ['studio', -4.2, 'bench'], ['concert-hall', 4.3, 'cases'], ['the-court', 4.4, 'bench']]) {
      const at = along(slug, d);
      const place = places.find((x) => x.slug === slug);
      if (at && place) items.push({ key: slug, at, kind, place });
    }
  }
  return (
    <group>
      {items.map((it) => <Placed key={it.key} at={it.at}>{draw[it.kind](it.place)}</Placed>)}
    </group>
  );
}
const BENCH = typeof document !== 'undefined' ? benchGeometry() : null;

/** Is someone performing on the plaza stage right now? (lights up for a show) */
function useStageLive() {
  const ref = useRef(false);
  const acc = useRef(1);
  const [, force] = React.useReducer((x) => x + 1, 0);
  useFrame((_, dt) => {
    acc.current += dt;
    if (acc.current < 1) return;
    acc.current = 0;
    const cs = useCityStore.getState();
    const t = serverNow();
    let live = false;
    for (const r of Object.values(cs.byId)) {
      for (const s of r.segs || []) {
        // The PLAZA's set (a set inside a venue, `r`, lights its own room, not this stage).
        if (s.sh && !s.r && (!s.p || s.p === 'street-plaza') && s.t0 <= t && t < s.t1) { live = true; break; }
      }
      if (live) break;
    }
    if (live !== ref.current) { ref.current = live; force(); }
  });
  return ref.current;
}

/**
 * THE GROUND, MEASURED (city/cityGround). The city model's ground is not at
 * y = 0 — its lots and pavements are at ~0.086 — and a floor laid at a guess
 * vanished under the lot it was built on. Once the city and its road map have
 * arrived, the ground is measured from the model, and everything here stands on
 * what was measured. Returns a number that changes when it has been.
 */
function useCityGround(selfRef) {
  const scene = useThree((st) => st.scene);
  const [v, setV] = useState(() => (groundMeasured() ? 1 : 0));
  const acc = useRef(1.2);
  useEffect(() => onGround(() => setV((x) => x + 1)), []);
  useFrame((_, dt) => {
    if (groundMeasured()) return;
    acc.current += dt;
    if (acc.current < 1.5) return;
    acc.current = 0;
    measureGround(scene, [selfRef.current].filter(Boolean));
  });
  return v;
}

// Inside the plaza and the park you stand on their floors, not the lot under them.
const rectOf = ({ at: [x, z], w, d }) => [x - w / 2, x + w / 2, z - d / 2, z + d / 2];
addDeck('street-plaza', rectOf(PLAZA), FLOOR_Y);
addDeck('city-park', rectOf(PARK), FLOOR_Y);

export default function CityPlaces() {
  const live = useStageLive();
  const self = useRef();
  const g = useCityGround(self);
  const plazaY = useMemo(() => baseGroundY(...PLAZA.at), [g]);
  const parkY = useMemo(() => baseGroundY(...PARK.at), [g]);
  return (
    <group ref={self}>
      <group position={[0, plazaY, 0]}><Plaza live={live} /></group>
      {/* The stage's NEXT SHOW board (its own feet: `g` re-seats it when the ground arrives). */}
      <StageSign g={g} />
      <group position={[0, parkY, 0]}><Park /></group>
      <VenueTouches ground={g} />
    </group>
  );
}
